import React, { useState } from "react";
import { Alert, Box, CircularProgress, InputAdornment, Link as MuiLink, Stack, TextField } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import ForgotPasswordDialog from "./ForgotPassword";
import { useAuth } from "../customAuth/authStateHook";
import { LoadingButton } from "@mui/lab";
import AuthShell from "./AuthShell";

export const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [openForgotPassword, setOpenForgotPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const handleTogglePassword = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleLogin = async (email: string, password: string) => {
        setShowAlert(false);
        setIsLoading(true);
        try {
            const success = await login(email, password);
            if (success) {
                navigate("/");
            } else {
                setShowAlert(true);
            }
        } catch (error) {
            console.error("Error signing in:", error);
            setShowAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Authentication"
            title="Welcome back"
            description="Sign in to continue managing collections, uploads, and searchable knowledge across your workspace."
        >
            <Stack spacing={2.25} useFlexGap>
                <TextField
                    fullWidth
                    value={email || ""}
                    onChange={(e) => {
                        setShowAlert(false);
                        setEmail(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleLogin(email, password);
                        }
                    }}
                    type="email"
                    placeholder="you@company.com"
                    label="Email"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailOutlinedIcon sx={{ color: "#8a6a4a", fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={authFieldStyles}
                />

                <TextField
                    fullWidth
                    value={password}
                    onChange={(e) => {
                        setShowAlert(false);
                        setPassword(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleLogin(email, password);
                        }
                    }}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    label="Password"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockOutlinedIcon sx={{ color: "#8a6a4a", fontSize: 18 }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleTogglePassword} edge="end">
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ ...authFieldStyles, mt: 2 }}
                />

                {showAlert && (
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: "16px",
                            border: "1px solid rgba(211, 47, 47, 0.16)",
                            bgcolor: "rgba(255, 244, 244, 0.9)",
                        }}
                    >
                        {!password && !email
                            ? "Please enter your email and password."
                            : "Invalid email address or password."}
                    </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
                    <MuiLink
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => setOpenForgotPassword(true)}
                        sx={{
                            color: "#6f675d",
                            fontSize: 13.5,
                            fontWeight: 500,
                            textUnderlineOffset: "4px",
                        }}
                    >
                        Forgot password?
                    </MuiLink>
                </Box>

                <LoadingButton
                    onClick={() => {
                        handleLogin(email, password);
                    }}
                    variant="contained"
                    loading={isLoading}
                    loadingIndicator={<CircularProgress size={22} sx={{ color: "#f7f1e8" }} />}
                    fullWidth
                    sx={{
                        mt: 0.5,
                        height: 52,
                        borderRadius: "16px",
                        textTransform: "none",
                        fontSize: 15,
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #2f271f 0%, #6b5136 100%)",
                        boxShadow: "0 16px 32px rgba(73, 57, 37, 0.18)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #241d17 0%, #5d452d 100%)",
                            boxShadow: "0 18px 38px rgba(73, 57, 37, 0.24)",
                        },
                    }}
                >
                    Sign in
                </LoadingButton>
            </Stack>
            <ForgotPasswordDialog open={openForgotPassword} handleClose={() => setOpenForgotPassword(false)} />
        </AuthShell>
    );
};

const authFieldStyles = {
    "& .MuiInputLabel-root": {
        color: "#6f675d",
        fontSize: 14,
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: "16px",
        backgroundColor: "#fffdf9",
        "& fieldset": {
            borderColor: "rgba(138, 106, 74, 0.18)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(138, 106, 74, 0.35)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#8a6a4a",
            boxShadow: "0 0 0 4px rgba(138, 106, 74, 0.10)",
        },
    },
    "& .MuiOutlinedInput-input": {
        py: 1.7,
        color: "#1f1a15",
    },
};
